import { IAlertProps } from "@components/alert/Alert.types";
import Skeleton from "@components/skeleton/Skeleton";
import { isNotDefined } from "@utils/general";
import React from "react";
import { WithTranslation, withTranslation } from "react-i18next";

import { TextAlign } from "../../enums";
import { IToString, TValue } from "../../global.types";
import { Validator } from "../../model/Validator";
import { IValidationError } from "../../model/Validator.types";
import TestIds from "../../testIds";
import { IconButton } from "../button";
import { EditIcon, IProps as IIconProps, SearchIcon } from "../icon";
import FormError from "../inputs/formError/FormError";
import { Label, SummaryItemStyled, Value, ValueWrapper } from "./Summary.styles";

export interface ISummaryItemProps {
    id: IToString;
    hotspotId?: string;
    label: string;
    value?: TValue;
    editable?: boolean;
    link?: string;
    isDisabled?: boolean;
    disabledAlert?: IAlertProps;
    isReadOnly?: boolean;
    color?: string;
    textAlign?: TextAlign;
    isSkeleton?: boolean;
    dataName?: string;
    hasAction?: boolean;
    onClick?: (id: IToString) => void;
    error?: IValidationError;
    icon?: React.ComponentType<IIconProps>;
    iconTitle?: string;
    isFullWidth?: boolean;
}

interface IProps extends ISummaryItemProps, WithTranslation {}

interface IPropsSkeleton extends Partial<IProps> {
    isSkeleton: true;
}

class SummaryItem extends React.PureComponent<IProps | IPropsSkeleton> {
    handleClick = () => {
        this.props.onClick?.(this.props.id);
    };

    render() {
        if (this.props.isSkeleton) {
            return <SummaryItemSkeleton/>;
        }

        const showButton = this.props.hasAction || (this.props.editable && !this.props.isReadOnly);

        if (isNotDefined(this.props.value) && !showButton) {
            return null;
        }

        const error = this.props.error && Validator.getErrorText(this.props.error, this.props.i18n);
        const Icon = this.props.icon ?? EditIcon;
        const hasLabel = !!this.props.label;
        const hasIcon = showButton || (this.props.link && !this.props.isReadOnly);

        const withoutPadding = hasLabel && hasIcon;

        return (
            <SummaryItemStyled
                textAlign={this.props.textAlign}
                isFullWidth={this.props.isFullWidth}
                data-testid={TestIds.SummaryItem}
                data-name={this.props.dataName}>
                {this.props.label &&
                        <Label isEditable={showButton}
                           hasLink={!!this.props.link}
                           data-testid={TestIds.SummaryItemLabel}>
                        {this.props.label}:
                    </Label>
                }
                <ValueWrapper textAlign={this.props.textAlign} hasPadding={!withoutPadding}>
                    <Value color={this.props.color} label={this.props.label} hasIcon={hasIcon}
                           hasPadding={!this.props.label}
                           data-testid={TestIds.SummaryItemValue}>
                        {this.props.value}
                        {error && <FormError errorText={error}/>}
                    </Value>
                    {showButton &&
                        <IconButton hotspotId={`${this.props.hotspotId ?? this.props.id}-summaryItemEdit`}
                                    title={this.props.iconTitle ?? this.props.t("Common:General.Edit")}
                                    onClick={this.handleClick}
                                    isDisabled={this.props.isDisabled}
                                    alert={this.props.disabledAlert}
                                    isTransparent>
                            <Icon/>
                        </IconButton>
                    }
                    {this.props.link && !this.props.isReadOnly &&
                        <IconButton hotspotId={`${this.props.hotspotId ?? this.props.id}-summaryItemLink`}
                                    title={this.props.iconTitle ?? this.props.t("Common:General.Open")}
                                    link={this.props.link}
                                    isDisabled={this.props.isDisabled}
                                    isTransparent>
                            <SearchIcon/>
                        </IconButton>
                    }
                </ValueWrapper>
            </SummaryItemStyled>
        );
    }
}

export default withTranslation(["Common"])(SummaryItem);

const SummaryItemSkeleton: React.FunctionComponent = () => {
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Skeleton width={"60px"} height={"16px"}/>
            <Skeleton width={"120px"} height={"32px"}/>
        </div>
    );
};